import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { GatsbyImage } from "gatsby-plugin-image";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";
import "swiper/css/navigation";

import { useStore } from "@Store/";

import Container from "@components/Container";

import IconArrowRight from "@svgs/IconArrowRight";

import {
  LeadershipCard,
  LeadershipCardArrow,
  LeadershipCardContent,
  LeadershipCardImage,
  LeadershipCardLink,
  LeadershipCardName,
  LeadershipCardRole,
  LeadershipHeader,
  LeadershipMain,
  LeadershipNavigation,
  LeadershipNavigationButton,
  LeadershipSlider,
  LeadershipTitle,
} from "./index.style";

gsap.registerPlugin(ScrollTrigger);

const OurLeaderShip = ({ list, id }) => {
  const [store] = useStore();

  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const previousButtonRef = useRef(null);
  const nextButtonRef = useRef(null);
  const swiperRef = useRef(null);
  const smoothScrollRef = useRef(store?.smoothScroll);

  const sectionId = id || "our-team";

  smoothScrollRef.current = store?.smoothScroll;

  const refresh = () => {
    if (
      smoothScrollRef.current &&
      typeof smoothScrollRef.current.refresh === "function"
    ) {
      smoothScrollRef.current.refresh();
    } else {
      ScrollTrigger.refresh();
    }
  };

  const updateSwiper = () => {
    const swiper = swiperRef.current;

    if (!swiper || swiper.destroyed) return;

    swiper.updateSize();
    swiper.updateSlides();
    swiper.updateProgress();
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const section = sectionRef.current;
    const titleElement = titleRef.current;

    if (!section || !titleElement) return undefined;

    let refreshFrameId;

    const refreshAfterLayout = () => {
      if (refreshFrameId) {
        cancelAnimationFrame(refreshFrameId);
      }

      refreshFrameId = requestAnimationFrame(() => {
        updateSwiper();
        refresh();
      });
    };

    const resize = () => {
      gsap.matchMediaRefresh();
      refreshAfterLayout();
    };

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add("(min-width:1024px)", () => {
        const titleTween = gsap.fromTo(
          titleElement,
          {
            x: -80,
            autoAlpha: 0,
          },
          {
            x: 0,
            autoAlpha: 1,
            duration: 1,
            ease: "power3.out",
            clearProps: "transform,opacity,visibility",
            scrollTrigger: {
              trigger: section,
              start: "top 80%",
              toggleActions: "play none none none",
              once: true,
              invalidateOnRefresh: true,
            },
          },
        );

        refreshAfterLayout();

        return () => {
          titleTween.scrollTrigger?.kill();
          titleTween.kill();
        };
      });

      mm.add("(max-width:1023px)", () => {
        gsap.set(titleElement, {
          clearProps: "all",
        });
      });

      refreshAfterLayout();
    }, section);

    window.addEventListener("load", refreshAfterLayout);
    window.addEventListener("resize", resize);

    return () => {
      if (refreshFrameId) {
        cancelAnimationFrame(refreshFrameId);
      }

      ctx.revert();

      window.removeEventListener(
        "load",
        refreshAfterLayout,
      );
      window.removeEventListener("resize", resize);

      ScrollTrigger.clearScrollMemory();
    };
  }, []);

  const renderMemberContent = (member) => (
    <>
      <LeadershipCardImage>
        {member?.image?.gatsbyImageData && (
          <GatsbyImage
            image={member.image.gatsbyImageData}
            alt={
              member.image.description ||
              member?.name ||
              ""
            }
          />
        )}
      </LeadershipCardImage>

      <LeadershipCardContent>
        <div>
          <LeadershipCardName>
            {member?.name}
          </LeadershipCardName>

          <LeadershipCardRole>
            {member?.role}
          </LeadershipCardRole>
        </div>

        {member?.linkedInUrl && (
          <LeadershipCardArrow aria-hidden="true">
            <IconArrowRight />
          </LeadershipCardArrow>
        )}
      </LeadershipCardContent>
    </>
  );

  return (
    <LeadershipMain
      id={sectionId}
      ref={sectionRef}
      aria-labelledby={`${sectionId}-title`}
    >
      <Container>
        <LeadershipHeader>
          <LeadershipTitle
            id={`${sectionId}-title`}
            ref={titleRef}
          >
            <span>Our</span>

            <strong>leadership</strong>
          </LeadershipTitle>

          <LeadershipNavigation
            aria-label="Leadership slider navigation"
          >
            <LeadershipNavigationButton
              ref={previousButtonRef}
              type="button"
              aria-label="Previous leadership member"
            >
              Previous
            </LeadershipNavigationButton>

            <LeadershipNavigationButton
              ref={nextButtonRef}
              type="button"
              aria-label="Next leadership member"
            >
              Next
            </LeadershipNavigationButton>
          </LeadershipNavigation>
        </LeadershipHeader>

      {list.length > 0 && (
        <LeadershipSlider>
          <Swiper
            modules={[Navigation]}
            slidesPerView="auto"
            spaceBetween={16}
            speed={700}
            watchOverflow
            grabCursor
            observer
            observeParents
            resizeObserver
            navigation={{
              prevEl: previousButtonRef.current,
              nextEl: nextButtonRef.current,
            }}
            onBeforeInit={(swiper) => {
              swiper.params.navigation.prevEl =
                previousButtonRef.current;

              swiper.params.navigation.nextEl =
                nextButtonRef.current;
            }}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;

              requestAnimationFrame(() => {
                updateSwiper();
                refresh();
              });
            }}
            onImagesReady={() => {
              requestAnimationFrame(() => {
                updateSwiper();
                refresh();
              });
            }}
            breakpoints={{
              768: {
                spaceBetween: 24,
              },
              1024: {
                spaceBetween: 36,
              },
            }}
          >
            {list.map((member, index) => (
              <SwiperSlide
                key={
                  member?.id ||
                  member?.contentful_id ||
                  index
                }
              >
                <LeadershipCard>
                  {member?.linkedInUrl ? (
                    <LeadershipCardLink
                      href={member.linkedInUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${
                        member?.name || "Team member"
                      } on LinkedIn`}
                    >
                      {renderMemberContent(member)}
                    </LeadershipCardLink>
                  ) : (
                    renderMemberContent(member)
                  )}
                </LeadershipCard>
              </SwiperSlide>
            ))}
          </Swiper>
        </LeadershipSlider>
      )}
      </Container>
    </LeadershipMain>
  );
};

OurLeaderShip.propTypes = {
  id: PropTypes.string,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      contentful_id: PropTypes.string,
      name: PropTypes.string,
      role: PropTypes.string,
      linkedInUrl: PropTypes.string,
      image: PropTypes.shape({
        description: PropTypes.string,
        gatsbyImageData: PropTypes.object,
      }),
    }),
  ),
};

OurLeaderShip.defaultProps = {
  id: "our-leadership",
  list: [],
};

export default OurLeaderShip;